
import { Options, Vue } from "vue-class-component";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import InputText from "primevue/inputtext";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import Message from "primevue/message";
import Dialog from "primevue/dialog";
import Field from "@/components/Field.vue";
import EmdAddressService from "@/service/emdAddress";
import AtaTmpPat from "@/model/ataTmpPat";
import AtaTmpPatUtil from "@/util/ataTmpPatUtil";
import DnaValidation from "@/model/dnaValidation";
import Ethnicity from "@/model/enums/ethnicity";
import Gender from "@/model/enums/gender";
import Abo from "@/model/enums/abo";
import Rh from "@/model/enums/rh";
import DgEmdis from "@/model/enums/dgemdis";
import SearchScope from "@/model/enums/searchScope";
import Dispha from "@/model/enums/dispha";
import Cmv from "@/model/enums/cmv";
import Status from "@/model/enums/status";
import EmdAddress from "@/model/emdAddress";
import PatientMode from "@/util/enums/patientMode";
import Alert from "@/util/alert";
import { namespace } from "vuex-class";
const ataTmpPat = namespace("ataTmpPat");

@Options({
  components: {
    Field,
    TabView,
    TabPanel,
    InputText,
    Calendar,
    Dropdown,
    Message,
    Dialog
  },
  watch: {
    "selected.personalnumber": {
      handler: "parsePersonalnumber"
    }
  }
})
export default class EditPatient extends Vue {
  @ataTmpPat.State
  selected!: AtaTmpPat;
  @ataTmpPat.State
  validationMap!: Map<string, string[]>;
  @ataTmpPat.State
  alerts!: Alert[];
  @ataTmpPat.State
  mode!: PatientMode;

  Gender = Gender;
  Ethnicity = Ethnicity;
  Abo = Abo;
  Rh = Rh;
  DgEmdis = DgEmdis;
  SearchScope = SearchScope;
  Dispha = Dispha;
  Cmv = Cmv;
  Status = Status;
  dnaLaboratory = [] as EmdAddress[];
  PatientMode = PatientMode;

  idclass = "p-col-6 p-mb-2";
  medicalclass = "p-col-3 p-mb-2";
  dnaclass = "p-col-2 p-mb-2 p-mr-2";
  idWidth = "150px";
  medicalWidth = "250px";
  dnaWidth = "100px";
  dnaBottomWidth = "200px";

  async created() {
    this.valid();
    await EmdAddressService.dnaLaboratory().then(
      resp => (this.dnaLaboratory = resp.data)
    );
  }

  getProps(field: string, css: string) {
    let disabled = field === "patientid1" ? !this.disable : this.disable;
    disabled =
      this.mode === PatientMode.EDIT && field === "personalnumber" // Disable personal number when EDIT
        ? true
        : disabled;
    return {
      disabled,
      class: this.getclass(field, css),
      id: field
    };
  }

  getFixedProps(width: string) {
    return {
      class: "p-col-fixed",
      style: "width:" + width
    };
  }

  getTooltip(field: string) {
    return this.validationMap.get(field);
  }

  getclass(val: string, def: string) {
    return [def, { "p-invalid": this.validationMap.get(val) }];
  }

  get disable() {
    return (
      this.mode === PatientMode.NONE ||
      this.mode === PatientMode.OTHER_TC ||
      this.mode === PatientMode.DUPLICATE ||
      this.mode === PatientMode.EDIT_CONFIRM
    );
    // return false;
  }

  get yearRange() {
    return `${new Date().getFullYear() - 150}:${new Date().getFullYear()}`;
  }

  closingEditPatDialog(edit: boolean) {
    if (edit) this.setMode(PatientMode.EDIT);
    else this.setMode(PatientMode.NONE);
    this.valid();
  }

  @ataTmpPat.Mutation("SET_MODE")
  setMode!: (mode: PatientMode) => void;

  @ataTmpPat.Mutation("ADD_ERROR")
  addError!: ({ field, msg }: { field: string; msg: string }) => void;

  @ataTmpPat.Mutation("RESET_ERROR")
  resetValidation!: () => void;

  async confirm() {
    await this.patientExists(this.selected.patientid1);
    this.valid();
  }

  async valid() {
    this.resetValidation();
    if (!this.selected.patientid1) {
      this.addError({ field: "patientid1", msg: "" });
      this.setMode(PatientMode.NONE);
    }
    if (this.disable) return;

    // this.parseFields();
    // Id tab
    if (!this.selected.lastname || this.selected.lastname.length == 0)
      this.addError({ field: "lastname", msg: "Required" });
    else if (this.selected.lastname.length > 30)
      this.addError({ field: "lastname", msg: "Max 30 characters" });

    if (!this.selected.firstname || this.selected.firstname.length == 0)
      this.addError({ field: "firstname", msg: "Required" });
    else if (this.selected.firstname.length > 30)
      this.addError({ field: "firstname", msg: "Max 30 characters" });

    if (!this.birthdate) this.addError({ field: "birthdate", msg: "Required" });
    else if (!(this.birthdate instanceof Date))
      this.addError({ field: "birthdate", msg: "Not a date" });

    if (
      (!this.selected.gender && this.selected.gender != 0) ||
      this.selected.gender == 2
    )
      this.addError({ field: "gender", msg: "Required" });

    if (this.selected.weight != null && isNaN(+this.selected.weight))
      this.addError({ field: "weight", msg: "Must be number" });

    if (this.selected.height != null && isNaN(+this.selected.height))
      this.addError({ field: "height", msg: "Must be number" });

    // Medical tab
    if (!this.selected.dgemdis || this.selected.dgemdis == 0)
      this.addError({ field: "dgemdis", msg: "Required" });

    if (this.dateofdg && !(this.dateofdg instanceof Date))
      this.addError({ field: "dateofdg", msg: "Not a date" });
    if (this.statusdate && !(this.statusdate instanceof Date))
      this.addError({ field: "statusdate", msg: "Not a date" });

    if (!this.selected.searchscope || this.selected.searchscope == 0)
      this.addError({ field: "searchscope", msg: "Required" });

    // DNA tab
    if (this.datetestdna && !(this.datetestdna instanceof Date))
      this.addError({ field: "datetestdna", msg: "Not a date" });

    await this.validateDna();
  }

  parseFields() {
    this.selected.a1 = this.parseDna(this.selected.a1);
    this.selected.a2 = this.parseDna(this.selected.a2);
    this.selected.b1 = this.parseDna(this.selected.b1);
    this.selected.b2 = this.parseDna(this.selected.b2);
    this.selected.c1 = this.parseDna(this.selected.c1);
    this.selected.c2 = this.parseDna(this.selected.c2);
    this.selected.drb11 = this.parseDna(this.selected.drb11);
    this.selected.drb12 = this.parseDna(this.selected.drb12);
    this.selected.drb31 = this.parseDna(this.selected.drb31);
    this.selected.drb32 = this.parseDna(this.selected.drb32);
    this.selected.drb41 = this.parseDna(this.selected.drb41);
    this.selected.drb42 = this.parseDna(this.selected.drb42);
    this.selected.drb51 = this.parseDna(this.selected.drb51);
    this.selected.drb52 = this.parseDna(this.selected.drb52);
    this.selected.dqb11 = this.parseDna(this.selected.dqb11);
    this.selected.dqb12 = this.parseDna(this.selected.dqb12);
    this.selected.dqa11 = this.parseDna(this.selected.dqa11);
    this.selected.dqa12 = this.parseDna(this.selected.dqa12);
    this.selected.dpb11 = this.parseDna(this.selected.dpb11);
    this.selected.dpb12 = this.parseDna(this.selected.dpb12);
    this.selected.dpa11 = this.parseDna(this.selected.dpa11);
    this.selected.dpa12 = this.parseDna(this.selected.dpa12);
  }

  validPersonalnumber() {
    const pn = this.selected.personalnumber;
    if (!/^\d{6}[-A+]{1}\d{3}[\w\d]{1}$/g.test(pn)) return false;

    const s = "0123456789ABCDEFHJKLMNPRSTUVWXY";
    if (!s.includes(pn.charAt(10))) return false;
    const c = pn.slice(0, 6) + pn.slice(7, 10);
    if (isNaN(+c)) return false;
    if (+c % 31 >= s.length) return false;
    return s[+c % 31] == pn.charAt(10);
  }

  async validateDna() {
    const dna: DnaValidation = {
      a1: this.selected.a1,
      a2: this.selected.a2,
      b1: this.selected.b1,
      b2: this.selected.b2,
      c1: this.selected.c1,
      c2: this.selected.c2,
      drb11: this.selected.drb11,
      drb12: this.selected.drb12,
      drb31: this.selected.drb31,
      drb32: this.selected.drb32,
      drb41: this.selected.drb41,
      drb42: this.selected.drb42,
      drb51: this.selected.drb51,
      drb52: this.selected.drb52,
      dqb11: this.selected.dqb11,
      dqb12: this.selected.dqb12,
      dqa11: this.selected.dqa11,
      dqa12: this.selected.dqa12,
      dpb11: this.selected.dpb11,
      dpb12: this.selected.dpb12,
      dpa11: this.selected.dpa11,
      dpa12: this.selected.dpa12
    };
    const errors = this.validationMap.size;
    let empty = true;
    Object.entries(dna).forEach(([key, value]) => {
      if (value) empty = false;
      if (!this.validDna(value))
        this.addError({
          field: key,
          msg: "Invalid Dna"
        });
    });
    if (!empty && errors == this.validationMap.size) {
      await this.dnaValidation(dna);
    }
  }

  validDna(dna: string): boolean {
    if (!dna || dna.length == 0) return true;
    if (!/^([\dA-Z]{2,5}:){0,3}[\dA-Z]{2,5}$/g.test(dna)) return false;
    return true;
  }

  parseDna(dna: string): string {
    dna = dna
      .trimStart()
      .toUpperCase()
      .replaceAll(" ", ":")
      .replaceAll(/[^\d\w:]/g, "");
    while (dna.includes("::")) dna = dna.replaceAll("::", ":");
    //TODO 2 -> 02
    return dna;
  }

  parsePersonalnumber() {
    if (this.selected.personalnumber == undefined) return; //When pat is saved, selected is removed which fire watcher one last time
    if (this.validPersonalnumber()) {
      const pn = this.selected.personalnumber;

      this.selected.gender = ((+pn.slice(8, 10) % 2) + 1) % 2; //Odd => 0 (Male), Even => 1 (Female)

      let century = "";
      switch (pn.charAt(6)) {
        case "+":
          century = "18";
          break;
        case "-":
          century = "19";
          break;
        case "A":
          century = "20";
          break;
      }
      this.selected.birthdate = new Date(
        +(century + pn.slice(4, 6)),
        +pn.slice(2, 4) - 1,
        +pn.slice(0, 2)
      );
    }
  }

  utcDate(date: Date | undefined): Date | undefined {
    if (date instanceof Date) {
      date = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
    }
    return date;
  }

  get birthdate() {
    return AtaTmpPatUtil.parseDate(this.selected.birthdate);
  }

  set birthdate(value: Date | undefined) {
    this.selected.birthdate = this.utcDate(value);
  }

  get dateofdg() {
    return AtaTmpPatUtil.parseDate(this.selected.dateofdg);
  }

  set dateofdg(value) {
    this.selected.dateofdg = this.utcDate(value);
  }

  get statusdate() {
    return AtaTmpPatUtil.parseDate(this.selected.statusdate);
  }

  set statusdate(value) {
    this.selected.statusdate = this.utcDate(value);
  }

  get datetestdna() {
    return AtaTmpPatUtil.parseDate(this.selected.datetestdna);
  }

  set datetestdna(value) {
    this.selected.datetestdna = this.utcDate(value);
  }

  @ataTmpPat.Action
  patientExists!: (tNumber: string) => void;

  @ataTmpPat.Action
  dnaValidation!: (dna: DnaValidation) => void;

  @ataTmpPat.Action
  save!: () => void;
}
